import { NoColorSpace, Object3D, TextureLoader } from 'three';
import { createDeck, type Card } from '../../engine/card.js';
import { findPileWithCard, type GameState, type Pile } from '../../engine/game-state.js';
import { applyMove, type Move } from '../../engine/moves.js';
import { shuffle } from '../../engine/shuffle.js';
import type { SolitaireRules } from '../../engine/rules.js';
import { createScene } from './scene.js';
import { createTable } from './table.js';
import {
  CARD_DEPTH,
  CARD_WIDTH,
  createCardBack,
  createCardFront,
  createCardMesh,
  createEdgeMaterial,
} from './card-mesh.js';
import { LAYOUT, type PileSlot } from './layout.js';
export { LAYOUT, type PileSlot } from './layout.js';
import { loadCardAtlas } from './card-faces.js';
import {
  finishAllTweens,
  flipTween,
  hasActiveTweens,
  tweenArc,
  tweenRotation,
  tweenTo,
  updateTweens,
} from './anim.js';
import { attachDrag } from './drag.js';
import { createSlotMarker } from './slot-marker.js';
import { playFlip, playPlace, playShuffle, unlockAudio } from '../../audio/sounds.js';
import { createHistory } from './history.js';
import { createHud } from './hud.js';
import { createDailyCalendar } from '../shared/daily-calendar.js';
import { markCompleted, seedForDateStr } from '../shared/daily-record.js';
import { createWinCascade } from './win-cascade.js';
import { createShadowPass } from './shadow-pass.js';
import { applyHudVisibility } from '../shared/hud-toggle.js';

// `commit: false` means "rerender (e.g. selection changed) without committing
// a move to history or counting it." Pyramid uses this for pair-select clicks.
export type PileClickResult =
  | false
  | true
  | { snap?: boolean; commit?: boolean };

export interface GameSpec {
  rules: SolitaireRules;
  slotForPile(pileId: string): PileSlot;
  dropExclude(): readonly string[];
  dragStackIds(state: GameState, cardId: string): string[];
  autoSendOrder(): readonly string[];
  maxFanLength: number;
  /** Variant deck factory. Defaults to `createDeck()` (52 cards). Spider
   * overrides this with a 104-card double-deck. */
  deckFactory?(): Card[];
  onPileClick?(state: GameState, pile: Pile, cardId: string): PileClickResult;
  statusText?(state: GameState): string | null;
  cardOffset?(pile: Pile, indexInPile: number): { dx: number; dz: number; dy?: number };
  cascadeCardIds?(state: GameState): string[];
  showSlotMarker?(pileId: string): boolean;
  /** Multiplier applied on top of the global camera padding, for layouts that
   * want extra breathing room (e.g. Pyramid's tall 7-row triangle). 1.0 = no
   * extra, 1.10 = 10% farther back. Defaults to 1.0. */
  cameraPad?: number;
  /** Override the landscape baseline elevation (degrees) - taller layouts
   * want a steeper top-down angle so the back of the table doesn't crop. */
  cameraElevationDeg?: number;
}


export async function mountGame(spec: GameSpec): Promise<void> {
  applyHudVisibility();
  const canvas = document.getElementById('stage') as HTMLCanvasElement | null;
  if (!canvas) throw new Error('missing #stage canvas');

  const { scene, camera, renderer, loadHDRI, setBounds } = createScene(canvas);

  const deckFactory = spec.deckFactory ?? (() => createDeck());
  let state = spec.rules.setup(shuffle(deckFactory()));
  // When the current game is a "daily" deal, this holds its YYYY-MM-DD date so a
  // win can be recorded. null for ordinary random/replay games.
  let dailyDate: string | null = null;
  const initialBounds = computeBounds();
  setBounds(initialBounds);

  function computeBounds() {
    const cw = LAYOUT.CARD_WIDTH;
    const ch = LAYOUT.CARD_HEIGHT;
    let minX = Infinity, maxX = -Infinity, minZ = Infinity, maxZ = -Infinity;
    for (const pileId of Object.keys(state.piles)) {
      const slot = spec.slotForPile(pileId);
      minX = Math.min(minX, slot.x - cw / 2);
      maxX = Math.max(maxX, slot.x + cw / 2);
      minZ = Math.min(minZ, slot.z - ch / 2);
      const fanZ = slot.fanDown ? (spec.maxFanLength - 1) * LAYOUT.FAN_DOWN : 0;
      maxZ = Math.max(maxZ, slot.z + fanZ + ch / 2);
    }
    const pad = spec.cameraPad ?? 1.0;
    const baseW = maxX - minX;
    const baseD = maxZ - minZ;
    return {
      width: baseW * pad,
      depth: baseD * pad,
      centerX: (minX + maxX) / 2,
      centerZ: (minZ + maxZ) / 2,
      baselineElevationDeg: spec.cameraElevationDeg,
    };
  }

  const loader = new TextureLoader();
  const feltNormal = loader.load('/textures/felt/normal.webp');
  feltNormal.colorSpace = NoColorSpace;
  const feltRough = loader.load('/textures/felt/rough.webp');
  feltRough.colorSpace = NoColorSpace;
  scene.add(createTable({ feltNormal, feltRoughness: feltRough, textureRepeat: 6, normalScale: 0.9 }));

  loadHDRI('/hdri/studio_small_09_512.hdr').catch((err) => {
    console.warn('HDRI load failed, falling back to ambient', err);
  });

  const atlas = await loadCardAtlas();
  const sharedBack = createCardBack(atlas);
  const sharedEdge = createEdgeMaterial();

  const groupById = new Map<string, Object3D>();
  const idByGroup = new Map<Object3D, string>();
  const history = createHistory();

  // Single render-to-texture contact shadow for the whole table. One soft
  // silhouette of all cards combined, density falls off as cards lift - exact
  // opposite of the per-card blob smear that piles into asymmetric shadows on
  // stacks. Padded a bit beyond the play bounds so the blur kernel has room.
  const shadowPass = createShadowPass({
    renderer,
    scene,
    area: {
      width: initialBounds.width + 2,
      depth: initialBounds.depth + 2,
      centerX: initialBounds.centerX,
      centerZ: initialBounds.centerZ,
    },
    resolution: 1024,
    height: 3,
    blur: 6,
    darkness: 1.0,
    opacity: 0.8,
  });
  scene.add(shadowPass.plane);
  let shadowDirty = true;

  function buildMeshes(cards: Card[]) {
    for (const card of cards) {
      const front = createCardFront(atlas, card);
      const group = createCardMesh({ front, back: sharedBack, edge: sharedEdge });
      group.userData.cardId = card.id;
      group.traverse((c) => { c.userData.cardId = card.id; });
      groupById.set(card.id, group);
      idByGroup.set(group, card.id);
      scene.add(group);
    }
  }

  function targetForCard(pileId: string, indexInPile: number) {
    const slot = spec.slotForPile(pileId);
    const pile = state.piles[pileId];
    const off = pile && spec.cardOffset ? spec.cardOffset(pile, indexInPile) : null;
    const dx = off ? off.dx : 0;
    const dz = off ? off.dz : 0;
    const dy = off?.dy ?? 0;
    return {
      x: slot.x + dx,
      y: CARD_DEPTH / 2 + (slot.y ?? 0) + indexInPile * LAYOUT.STACK_LIFT + dy,
      z: (slot.fanDown ? slot.z + indexInPile * LAYOUT.FAN_DOWN : slot.z) + dz,
    };
  }

  function rotationForCard(card: Card) {
    return { x: 0, y: 0, z: card.faceUp ? 0 : Math.PI };
  }

  function snapAll() {
    for (const pile of Object.values(state.piles)) {
      pile.cards.forEach((card, i) => {
        const group = groupById.get(card.id);
        if (!group) return;
        const t = targetForCard(pile.id, i);
        const r = rotationForCard(card);
        group.position.set(t.x, t.y, t.z);
        group.rotation.set(r.x, r.y, r.z);
      });
    }
    shadowDirty = true;
  }

  function tweenAll(durationMs = 260) {
    finishAllTweens();
    let anyFlip = false;
    for (const pile of Object.values(state.piles)) {
      pile.cards.forEach((card, i) => {
        const group = groupById.get(card.id);
        if (!group) return;
        const t = targetForCard(pile.id, i);
        const r = rotationForCard(card);
        const zDelta = Math.abs(((group.rotation.z - r.z) % (2 * Math.PI)));
        const flipping = zDelta > 0.1 && zDelta < 2 * Math.PI - 0.1;
        if (flipping) {
          anyFlip = true;
          flipTween(group, t, r.z, Math.max(durationMs, 180), CARD_WIDTH);
          tweenRotation(group, { x: r.x, y: r.y }, durationMs);
        } else {
          const dist = Math.hypot(t.x - group.position.x, t.z - group.position.z);
          if (dist > 0.05) {
            const peakLift = Math.min(0.35, dist * 0.35);
            tweenArc(group, t, durationMs, peakLift);
          } else {
            tweenTo(group, t, durationMs);
          }
          tweenRotation(group, r, durationMs);
        }
      });
    }
    if (anyFlip) playFlip();
    shadowDirty = true;
  }

  for (const pile of Object.values(state.piles)) {
    if (spec.showSlotMarker && !spec.showSlotMarker(pile.id)) continue;
    const slot = spec.slotForPile(pile.id);
    const marker = createSlotMarker();
    marker.position.set(slot.x, marker.position.y, slot.z);
    scene.add(marker);
  }

  buildMeshes(Object.values(state.piles).flatMap((p) => p.cards));
  snapAll();

  const hud = createHud({
    statusText: () => spec.statusText?.(state) ?? null,
    onUndo: () => undo(),
    onNewGame: () => newGame(),
    onDailyGame: () => dailyCalendar.open(),
    onReplay: () => newGame(),
  });

  const dailyCalendar = createDailyCalendar({
    variant: spec.rules.id,
    onPlay: (date) => startDaily(date),
  });

  const winCascade = createWinCascade({
    renderer,
    onComplete: () => {
      hud.showWin();
      hud.setUndoEnabled(false);
    },
  });

  function refreshUndo() {
    hud.setUndoEnabled(history.size() > 0 && !winCascade.isActive());
  }
  refreshUndo();

  function afterStateChange() {
    hud.recordMove();
    refreshUndo();
    if (spec.rules.isWon(state) && !winCascade.isActive()) {
      if (dailyDate) markCompleted(spec.rules.id, dailyDate);
      const ids = spec.cascadeCardIds
        ? spec.cascadeCardIds(state)
        : Object.values(state.piles)
            .filter((p) => p.type === 'foundation')
            .flatMap((p) => p.cards.map((c) => c.id));
      const groups = ids
        .map((id) => groupById.get(id))
        .filter((g): g is Object3D => !!g);
      const b = computeBounds();
      winCascade.start(groups, { x: b.centerX, z: b.centerZ });
      refreshUndo();
    } else if (spec.rules.isLost?.(state)) {
      hud.refreshStatus();
      hud.showLose();
    } else {
      hud.refreshStatus();
    }
  }

  function pileOf(cardId: string): Pile | undefined {
    return findPileWithCard(state, cardId);
  }

  function undo() {
    if (winCascade.isActive()) return;
    const prev = history.pop();
    if (!prev) return;
    state = prev;
    hud.refreshStatus();
    refreshUndo();
    tweenAll();
  }

  function dragStackFor(picked: Object3D): Object3D[] {
    const id = idByGroup.get(picked);
    if (!id) return [];
    const ids = spec.dragStackIds(state, id);
    return ids.map((i) => groupById.get(i)).filter((m): m is Object3D => !!m);
  }

  function findDropPile(x: number, z: number, fromPile: Pile, cardId: string): string | null {
    const exclude = new Set([fromPile.id, ...spec.dropExclude()]);
    let bestLegal: { id: string; d: number } | null = null;
    let bestAny: { id: string; d: number } | null = null;
    for (const id of Object.keys(state.piles)) {
      if (exclude.has(id)) continue;
      const slot = spec.slotForPile(id);
      const pile = state.piles[id]!;
      let cz = slot.z;
      if (slot.fanDown && pile.cards.length > 0) {
        cz = slot.z + (pile.cards.length - 1) * LAYOUT.FAN_DOWN;
      }
      const d = Math.hypot(x - slot.x, z - cz);
      if (d > CARD_WIDTH * 1.5) continue;
      if (!bestAny || d < bestAny.d) bestAny = { id, d };
      if (spec.rules.canMove(state, { from: fromPile.id, to: id, cardId })) {
        if (!bestLegal || d < bestLegal.d) bestLegal = { id, d };
      }
    }
    return bestLegal?.id ?? bestAny?.id ?? null;
  }

  function autoSend(cardId: string): boolean {
    const from = pileOf(cardId);
    if (!from) return false;
    for (const toId of spec.autoSendOrder()) {
      if (toId === from.id) continue;
      const move: Move = { from: from.id, to: toId, cardId };
      if (!spec.rules.canMove(state, move)) continue;
      const next = applyMove(state, move, spec.rules);
      if (!next) continue;
      history.push(state);
      state = next;
      playPlace();
      tweenAll();
      afterStateChange();
      return true;
    }
    return false;
  }

  attachDrag({
    canvas,
    camera,
    cardMeshes: () => Array.from(groupById.values()),
    getDragStack: dragStackFor,
    onDrop: (lead, x, z) => {
      if (winCascade.isActive()) return false;
      const id = idByGroup.get(lead);
      if (!id) return false;
      const from = pileOf(id);
      if (!from) return false;
      const toId = findDropPile(x, z, from, id);
      if (!toId) return false;
      const move: Move = { from: from.id, to: toId, cardId: id };
      if (!spec.rules.canMove(state, move)) return false;
      const next = applyMove(state, move, spec.rules);
      if (!next) return false;
      history.push(state);
      state = next;
      playPlace();
      tweenAll();
      afterStateChange();
      return true;
    },
    onClick: (group) => {
      if (winCascade.isActive()) return;
      const id = idByGroup.get(group);
      if (!id) return;
      const pile = pileOf(id);
      if (!pile) return;
      if (!spec.onPileClick) return;
      history.push(state);
      const result = spec.onPileClick(state, pile, id);
      if (!result) {
        history.pop();
        return;
      }
      const opts = typeof result === 'object' ? result : {};
      const commit = opts.commit !== false;
      if (!commit) history.pop();
      if (opts.snap) snapAll(); else tweenAll();
      if (commit) afterStateChange();
    },
    onDoubleClick: (group) => {
      if (winCascade.isActive()) return;
      const id = idByGroup.get(group);
      if (!id) return;
      const pile = pileOf(id);
      if (!pile || pile.type === 'stock') return;
      autoSend(id);
    },
    onMove: () => {
      renderer.shadowMap.needsUpdate = true;
      shadowDirty = true;
    },
    onPickStart: () => {
      finishAllTweens();
    },
    onCancel: () => {
      tweenAll(180);
    },
  });

  function dealNew(seed?: number) {
    winCascade.cancel();
    for (const group of groupById.values()) scene.remove(group);
    groupById.clear();
    idByGroup.clear();
    history.clear();
    state = spec.rules.setup(shuffle(deckFactory(), seed));
    buildMeshes(Object.values(state.piles).flatMap((p) => p.cards));
    snapAll();
    hud.resetStats();
    hud.refreshStatus();
    refreshUndo();
    playShuffle();
  }

  function newGame(seed?: number) {
    dailyDate = null;
    dealNew(seed);
  }

  function startDaily(date: string) {
    dailyDate = date;
    dealNew(seedForDateStr(date));
  }

  window.addEventListener('pointerdown', () => unlockAudio(), { once: true });

  let prevActive = false;
  let prevTickTime = performance.now();
  function tick(t: number) {
    const now = performance.now();
    const dt = Math.min(0.05, (now - prevTickTime) / 1000);
    prevTickTime = now;
    updateTweens(t);
    winCascade.update(dt);
    const active = hasActiveTweens();
    const cascading = winCascade.isActive();
    // Re-bake the contact shadow whenever cards are moving (so a dragged
    // card's shadow recedes as it lifts) and one extra frame after they
    // settle to capture the final resting positions.
    if (shadowDirty || active || prevActive || cascading) {
      shadowPass.update();
      shadowDirty = active || cascading;
    }
    if (active || prevActive || cascading) renderer.shadowMap.needsUpdate = true;
    prevActive = active;
    renderer.render(scene, camera);
    requestAnimationFrame(tick);
  }
  requestAnimationFrame(tick);
}
